/**
 * Typisierte Hooks für `/api/v1/tickets` (Spec 06a, `domains/tasks`) — die
 * Aufgabe, ihre Liste über den ganzen Bestand, der Einzelabruf für das Öffnen
 * aus dem Kalender und der Zähler für `rail-kalender` (Spec 45).
 *
 * `fetch` steht ausschließlich hier — nie in einer Komponente
 * (`apps/web/CLAUDE.md`). Query-Keys kommen ausschließlich aus
 * `lib/query-keys.ts`; jede Mutation invalidiert über das Präfix
 * `keys.tickets.all`, nie einzeln aufgezählt — Anlegen, Ändern und „Erledigt"
 * können die Liste **und** `summary` zugleich verändern.
 *
 * Die Zuweisungsauswahl (`task-assignee`, `GET /access/users?can_see=`) liegt
 * seit Spec 06b nicht mehr hier: Mit `duedate-task-assignee` als zweitem
 * Aufrufer zog `components/AssigneeField.tsx` die Abfrage nach
 * `lib/assignable-users.ts` (`features/*` dürfen einander nicht importieren,
 * dieselbe Begründung wie bei `TaskCostCenterField` unten).
 *
 * Die Kostenstellenauswahl (`task-cost-center`) liegt seit Tor 2 von 006a
 * nicht mehr hier: `TaskCostCenterField` teilt sich jetzt `components/
 * EntityPicker.tsx` mit `access-invite-entity`, die Abfrage dahinter
 * `lib/entities.ts` (Simplifier-Fund, `docs/work/006a-aufgaben-2a164c/
 * findings.md`, „Tor 2").
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { components } from '@/lib/api-types'
import { request } from '@/lib/api'
import { keys } from '@/lib/query-keys'

export type TicketRow = components['schemas']['TicketRow']
export type TicketStatus = components['schemas']['TicketStatus']
export type TicketPriority = components['schemas']['TicketPriority']
export type TicketCreate = components['schemas']['TicketCreate']
export type TicketUpdate = components['schemas']['TicketUpdate']
export type TicketSummary = components['schemas']['TicketSummary']
export type TicketSubject = components['schemas']['TicketSubject']
export type TicketAssignee = components['schemas']['TicketAssignee']
export type TicketCostCenter = components['schemas']['TicketCostCenter']
export type LabeledValue = components['schemas']['LabeledValue']

interface QueryOptions {
  readonly enabled?: boolean
}

export interface TicketsPage {
  readonly items: TicketRow[]
  readonly next_cursor: string | null
}

export interface TicketsFilter {
  readonly scope?: string
  readonly status?: readonly TicketStatus[]
  /** `'me'` löst der Server auf (I9) — kein Client vergleicht eine Nutzer-Id
   * gegen die eigene. */
  readonly assignee?: string
  /** `?open=1` — „Status nicht in `CLOSED_STATUSES`", serverseitig. Der
   * Client zählt die offenen Status **nicht** selbst auf; welche das sind,
   * weiß er nicht und soll es nicht wissen (Befund B2, I9). */
  readonly open?: boolean
  readonly overdue?: boolean
  readonly limit?: number
  readonly cursor?: string
  readonly updatedSince?: string
}

/**
 * `GET /tickets` — dieselbe Abfrage für den Reiter *Aufgaben* eines Objekts
 * (`?scope=`) und für die Ansichten „Überfällig“ und „Meine Aufgaben“ des
 * Kalenders (Spec 45, `lib/kalender-view.ts::ticketsQueryForView`). Die
 * Anfrage wird **in** `queryFn` aus einzelnen `filter.*`-Feldern gebaut,
 * nicht über eine Hilfsfunktion, die das ganze `filter`-Objekt entgegennimmt
 * — sonst sieht `@tanstack/query/exhaustive-deps` nur „`filter` wird
 * benutzt" und verlangt es unverändert im `queryKey`, obwohl der Key bereits
 * jedes einzelne Feld trägt (dieselbe Bauform wie `useOpenItems` in
 * `features/billing/api.ts`).
 */
export function useTicketsList(filter: TicketsFilter, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.tickets.list({
      scope: filter.scope,
      status: filter.status,
      assignee: filter.assignee,
      open: filter.open ?? false,
      overdue: filter.overdue ?? false,
      limit: filter.limit,
      cursor: filter.cursor,
      updatedSince: filter.updatedSince,
    }),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams()
      if (filter.scope !== undefined) params.set('scope', filter.scope)
      for (const status of filter.status ?? []) params.append('status', status)
      if (filter.assignee !== undefined) params.set('assignee', filter.assignee)
      if (filter.open === true) params.set('open', 'true')
      if (filter.overdue === true) params.set('overdue', 'true')
      if (filter.limit !== undefined) params.set('limit', String(filter.limit))
      if (filter.cursor !== undefined) params.set('cursor', filter.cursor)
      if (filter.updatedSince !== undefined) params.set('updated_since', filter.updatedSince)
      const query = params.toString() === '' ? '' : `?${params.toString()}`
      return request<TicketsPage>(`/tickets${query}`, { signal })
    },
    enabled: options.enabled ?? true,
  })
}

/** Cursorfortsetzung für die Aufgaben-Facet eines Objekts. */
export function useInfiniteTicketsList(filter: TicketsFilter, options: QueryOptions = {}) {
  return useInfiniteQuery({
    queryKey: keys.tickets.list({
      scope: filter.scope,
      status: filter.status,
      assignee: filter.assignee,
      open: filter.open ?? false,
      overdue: filter.overdue ?? false,
      limit: filter.limit,
      updatedSince: filter.updatedSince,
    }),
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams()
      if (filter.scope !== undefined) params.set('scope', filter.scope)
      for (const status of filter.status ?? []) params.append('status', status)
      if (filter.assignee !== undefined) params.set('assignee', filter.assignee)
      if (filter.open === true) params.set('open', 'true')
      if (filter.overdue === true) params.set('overdue', 'true')
      if (filter.limit !== undefined) params.set('limit', String(filter.limit))
      if (filter.updatedSince !== undefined) params.set('updated_since', filter.updatedSince)
      if (pageParam !== null) params.set('cursor', pageParam)
      const query = params.toString() === '' ? '' : `?${params.toString()}`
      return request<TicketsPage>(`/tickets${query}`, { signal })
    },
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    enabled: options.enabled ?? true,
  })
}

/** `GET /tickets/summary` — die Kennzahl hinter `rail-kalender-attention` und
 * den Zählern der Kalender-Seitenleiste (Spec 45, R4; ADR 0011: „Kein Bereich
 * ohne eigenen Zähl-Endpunkt"). Nie aus einer Listenabfrage abgeleitet. */
export function useTicketSummary(options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.tickets.summary,
    queryFn: ({ signal }) => request<TicketSummary>('/tickets/summary', { signal }),
    enabled: options.enabled ?? true,
  })
}

/** `GET /tickets/{id}` — eine Aufgabe, die der Kalender öffnen will (Spec 45,
 * Regel 11): Der Eintrag trägt nur `source_id` und `version`, das Sheet braucht
 * die ganze Zeile. Ohne `id` (kein Eintrag angeklickt) ruht die Abfrage. */
export function useTicket(id: string | null) {
  return useQuery({
    queryKey: keys.tickets.detail(id ?? ''),
    queryFn: ({ signal }) => request<TicketRow>(`/tickets/${id ?? ''}`, { signal }),
    enabled: id !== null,
    // Kein Zwischenspeicher über das Schließen hinaus: Das Sheet sendet
    // `If-Match` mit `version` — ein veralteter Treffer aus dem Cache gäbe
    // beim Speichern 409. Nach dem Schließen verschwindet der Satz, das
    // nächste Öffnen lädt frisch.
    gcTime: 0,
  })
}

/** `GET /tickets/statuses` — ändert sich zur Laufzeit nie, dieselbe
 * Zurückhaltung wie `useRoles`/`useTermReasons`. Kein Client pflegt eine
 * eigene Zuordnung von Werten auf deutsche Bezeichnungen (`ARCHITECTURE.md`
 * 5, AK-394). */
export function useTicketStatuses() {
  return useQuery({
    queryKey: keys.tickets.statuses,
    queryFn: ({ signal }) => request<LabeledValue[]>('/tickets/statuses', { signal }),
    staleTime: Infinity,
  })
}

/** `GET /tickets/priorities` — ebenso. */
export function useTicketPriorities() {
  return useQuery({
    queryKey: keys.tickets.priorities,
    queryFn: ({ signal }) => request<LabeledValue[]>('/tickets/priorities', { signal }),
    staleTime: Infinity,
  })
}

interface CreateTicketArgs {
  readonly body: TicketCreate
}

export function useCreateTicket() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ body }: CreateTicketArgs) =>
      request<TicketRow>('/tickets', {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.tickets.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
      // Spec 07/45: jede Aufgabe hat `due_on` und steht als `kind = 'task'` im
      // Kalenderfenster — dieselbe Zusatz-Invalidierung wie
      // `features/schedule/api.ts` bei jedem Termin.
      void queryClient.invalidateQueries({ queryKey: keys.calendar.all })
    },
  })
}

interface UpdateTicketArgs {
  readonly ticketId: string
  readonly body: TicketUpdate
  readonly ifMatch: string
}

/** `PATCH /tickets/{id}` — Ändern **und** „Erledigt" (`task-done-<id>`)
 * laufen über denselben Aufruf, nur mit unterschiedlichem Rumpf. */
export function useUpdateTicket() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ ticketId, body, ifMatch }: UpdateTicketArgs) =>
      request<TicketRow>(`/tickets/${ticketId}`, {
        method: 'PATCH',
        body,
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.tickets.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
      // Spec 07: `PATCH` ändert `due_on` oder den Status, beides sichtbar im
      // Kalenderfenster (W-B2) — dieselbe Zusatz-Invalidierung wie
      // `features/schedule/api.ts` bei jeder Termin-Mutation.
      void queryClient.invalidateQueries({ queryKey: keys.calendar.all })
    },
  })
}
